
import { useState, useRef } from 'react';
import { petCategories } from '../../../mocks/petListings';

interface UploadListingModalProps {
  onClose: () => void;
}

export default function UploadListingModal({ onClose }: UploadListingModalProps) {
  const [formData, setFormData] = useState({
    petName: '',
    category: '',
    breed: '',
    age: '',
    gender: '',
    price: '',
    location: '',
    description: '',
    sellerName: '',
    email: '',
  });
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageName, setImageName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const categoryOptions = petCategories.filter((c) => c.id !== 'all');

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, image: 'Image must be under 10MB' }));
        return;
      }
      setImageName(file.name);
      setErrors((prev) => {
        const next = { ...prev };
        delete next.image;
        return next;
      });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.petName.trim()) newErrors.petName = 'Pet name is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.breed.trim()) newErrors.breed = 'Breed is required';
    if (!formData.age.trim()) newErrors.age = 'Age is required';
    if (!formData.gender) newErrors.gender = 'Gender is required';
    if (!formData.price.trim()) newErrors.price = 'Price is required';
    if (!formData.location.trim()) newErrors.location = 'Location is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (formData.description.length > 500) newErrors.description = 'Description must be under 500 characters';
    if (!formData.sellerName.trim()) newErrors.sellerName = 'Your name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append('petName', formData.petName);
      body.append('category', formData.category);
      body.append('breed', formData.breed);
      body.append('age', formData.age);
      body.append('gender', formData.gender);
      body.append('price', formData.price);
      body.append('location', formData.location);
      body.append('description', formData.description);
      body.append('sellerName', formData.sellerName);
      body.append('email', formData.email);
      body.append('image', imageName || 'Uncollectable');

      await fetch('https://readdy.ai/api/form/d6a6267mvg9ih2c7abkg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setErrors({ form: 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
        <div className="bg-white rounded-3xl max-w-md w-full p-10 text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
          <div className="w-20 h-20 bg-gradient-to-r from-[#9B4C9A] to-[#FF9BC8] rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-check-line text-white text-4xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-[#4A3B5C] mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Listing Submitted!
          </h2>
          <p className="text-sm text-[#8B7BA8] mb-6 leading-relaxed">
            Your pet listing has been submitted successfully. We&apos;ll review it and get back to you shortly.
          </p>
          <button
            onClick={onClose}
            className="bg-gradient-to-r from-[#9B4C9A] to-[#FF9BC8] text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-[#4A3B5C]" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <i className="ri-add-circle-line text-[#9B4C9A] mr-2"></i>
              List Your Pet
            </h2>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-[#F8F6FB] rounded-full flex items-center justify-center hover:bg-[#E6E0F5] transition-all cursor-pointer"
            >
              <i className="ri-close-line text-xl text-[#4A3B5C]"></i>
            </button>
          </div>

          {errors.form && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-xl text-sm font-medium">
              {errors.form}
            </div>
          )}

          <form
            id="pet-listing-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="space-y-5"
          >
            {/* Image Upload */}
            <div>
              <label className="block text-sm font-semibold text-[#4A3B5C] mb-2">Pet Photo</label>
              <div
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all hover:border-[#9B4C9A] hover:bg-[#F8F6FB] ${
                  imagePreview ? 'border-[#9B4C9A] bg-[#F8F6FB]' : 'border-[#E6E0F5]'
                }`}
              >
                {imagePreview ? (
                  <div className="relative">
                    <div className="w-full h-48 rounded-xl overflow-hidden mx-auto">
                      <img src={imagePreview} alt="Preview" className="w-full h-full object-cover object-top" />
                    </div>
                    <p className="text-xs text-[#8B7BA8] mt-3">Click to change image</p>
                  </div>
                ) : (
                  <>
                    <i className="ri-upload-cloud-2-line text-4xl text-[#9B4C9A] mb-2 block"></i>
                    <p className="text-sm font-semibold text-[#4A3B5C]">Click to upload an image</p>
                    <p className="text-xs text-[#8B7BA8] mt-1">JPG, PNG up to 10MB</p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  name="image"
                  accept="image/jpeg,image/png"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>
              {errors.image && <p className="text-xs text-red-500 mt-1">{errors.image}</p>}
            </div>

            {/* Row: Name + Category */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#4A3B5C] mb-2">Pet Name *</label>
                <input
                  type="text"
                  name="petName"
                  value={formData.petName}
                  onChange={(e) => handleChange('petName', e.target.value)}
                  placeholder="e.g. Max"
                  className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-[#9B4C9A]/30 focus:border-[#9B4C9A] transition-all ${
                    errors.petName ? 'border-red-400' : 'border-[#E6E0F5]'
                  }`}
                />
                {errors.petName && <p className="text-xs text-red-500 mt-1">{errors.petName}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#4A3B5C] mb-2">Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-[#9B4C9A]/30 focus:border-[#9B4C9A] transition-all cursor-pointer ${
                    errors.category ? 'border-red-400' : 'border-[#E6E0F5]'
                  }`}
                >
                  <option value="">Select category</option>
                  {categoryOptions.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
                {errors.category && <p className="text-xs text-red-500 mt-1">{errors.category}</p>}
              </div>
            </div>

            {/* Row: Breed + Age */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#4A3B5C] mb-2">Breed *</label>
                <input
                  type="text"
                  name="breed"
                  value={formData.breed}
                  onChange={(e) => handleChange('breed', e.target.value)}
                  placeholder="e.g. Golden Retriever"
                  className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-[#9B4C9A]/30 focus:border-[#9B4C9A] transition-all ${
                    errors.breed ? 'border-red-400' : 'border-[#E6E0F5]'
                  }`}
                />
                {errors.breed && <p className="text-xs text-red-500 mt-1">{errors.breed}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#4A3B5C] mb-2">Age *</label>
                <input
                  type="text"
                  name="age"
                  value={formData.age}
                  onChange={(e) => handleChange('age', e.target.value)}
                  placeholder="e.g. 2 years"
                  className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-[#9B4C9A]/30 focus:border-[#9B4C9A] transition-all ${
                    errors.age ? 'border-red-400' : 'border-[#E6E0F5]'
                  }`}
                />
                {errors.age && <p className="text-xs text-red-500 mt-1">{errors.age}</p>}
              </div>
            </div>

            {/* Row: Gender + Price */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#4A3B5C] mb-2">Gender *</label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={(e) => handleChange('gender', e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-[#9B4C9A]/30 focus:border-[#9B4C9A] transition-all cursor-pointer ${
                    errors.gender ? 'border-red-400' : 'border-[#E6E0F5]'
                  }`}
                >
                  <option value="">Select gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
                {errors.gender && <p className="text-xs text-red-500 mt-1">{errors.gender}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#4A3B5C] mb-2">Price ($) *</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={(e) => handleChange('price', e.target.value)}
                  placeholder="e.g. 500"
                  className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-[#9B4C9A]/30 focus:border-[#9B4C9A] transition-all ${
                    errors.price ? 'border-red-400' : 'border-[#E6E0F5]'
                  }`}
                />
                {errors.price && <p className="text-xs text-red-500 mt-1">{errors.price}</p>}
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-semibold text-[#4A3B5C] mb-2">Location *</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="e.g. Los Angeles, CA"
                className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-[#9B4C9A]/30 focus:border-[#9B4C9A] transition-all ${
                  errors.location ? 'border-red-400' : 'border-[#E6E0F5]'
                }`}
              />
              {errors.location && <p className="text-xs text-red-500 mt-1">{errors.location}</p>}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-[#4A3B5C] mb-2">Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    handleChange('description', e.target.value);
                  }
                }}
                placeholder="Tell us about your pet..."
                rows={3}
                maxLength={500}
                className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-[#9B4C9A]/30 focus:border-[#9B4C9A] transition-all resize-none ${
                  errors.description ? 'border-red-400' : 'border-[#E6E0F5]'
                }`}
              />
              <div className="flex justify-between mt-1">
                {errors.description && <p className="text-xs text-red-500">{errors.description}</p>}
                <p className="text-xs text-[#8B7BA8] ml-auto">{formData.description.length}/500</p>
              </div>
            </div>

            {/* Row: Seller Name + Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#4A3B5C] mb-2">Your Name *</label>
                <input
                  type="text"
                  name="sellerName"
                  value={formData.sellerName}
                  onChange={(e) => handleChange('sellerName', e.target.value)}
                  placeholder="e.g. John Smith"
                  className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-[#9B4C9A]/30 focus:border-[#9B4C9A] transition-all ${
                    errors.sellerName ? 'border-red-400' : 'border-[#E6E0F5]'
                  }`}
                />
                {errors.sellerName && <p className="text-xs text-red-500 mt-1">{errors.sellerName}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#4A3B5C] mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="e.g. john@email.com"
                  className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-[#9B4C9A]/30 focus:border-[#9B4C9A] transition-all ${
                    errors.email ? 'border-red-400' : 'border-[#E6E0F5]'
                  }`}
                />
                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-[#9B4C9A] to-[#FF9BC8] text-white py-4 rounded-xl font-semibold hover:shadow-lg hover:scale-[1.02] transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Submitting...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-fill"></i>
                  Submit Listing
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
